import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  loadPrefs as loadTonePrefs,
  categoryForType,
  getCustomToneUrl,
  clearObjectUrlCache,
} from '../utils/notificationTones';

const API = process.env.REACT_APP_BACKEND_URL;

// "2 minutes ago" / "just now" / "yesterday at 14:32". Local helper so
// we don't pull in date-fns just for one toast — the bell dropdown
// already uses formatDistanceToNow there, but importing it here
// would bloat this context with another tree-shake target.
const formatRelative = (iso) => {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const diffMs = Date.now() - t.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 30) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  // Older than a week → absolute short date, locale-friendly.
  return t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// ============ AUDIO SYSTEM FOR NOTIFICATIONS ============
let notificationAudioContext = null;

const initNotificationAudio = () => {
  try {
    if (!notificationAudioContext || notificationAudioContext.state === 'closed') {
      notificationAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (notificationAudioContext.state === 'suspended') {
      notificationAudioContext.resume();
    }
    return notificationAudioContext;
  } catch (error) {
    console.error('[NOTIFICATION_AUDIO] AudioContext init failed:', error);
    return null;
  }
};

// Async version that AWAITS resume() so callers can be sure ctx.state ===
// 'running' before they try to schedule oscillators. The synchronous
// version above leaves resume() pending, which on Chrome's first click
// means `playNotificationSound` sees state === 'suspended' and bails. We
// keep both because most existing call sites are sync and work fine once
// the first user gesture has primed the context.
const ensureAudioRunning = async () => {
  try {
    if (!notificationAudioContext || notificationAudioContext.state === 'closed') {
      notificationAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (notificationAudioContext.state === 'suspended') {
      await notificationAudioContext.resume();
    }
    return notificationAudioContext;
  } catch (error) {
    console.error('[NOTIFICATION_AUDIO] Resume failed:', error);
    return null;
  }
};

// Play a pleasant notification sound (different from Live Chat ring)
const playNotificationSound = (type = 'comment', volume = 0.4) => {
  try {
    const ctx = initNotificationAudio();
    if (!ctx || ctx.state !== 'running') {
      return false;
    }

    const now = ctx.currentTime;
    
    if (type === 'comment' || type === 'note') {
      // Pleasant chime for comments - C5, E5, G5 chord
      const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        const startTime = now + (i * 0.08);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(volume * 0.6, startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);
        osc.start(startTime);
        osc.stop(startTime + 0.45);
      });
    } else if (type === 'new_lead_received' || type === 'owner_assigned') {
      // Prominent alert for new lead assignments - ascending arpeggio
      const frequencies = [392.00, 523.25, 659.25, 783.99]; // G4, C5, E5, G5
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'triangle'; // Warmer tone
        const startTime = now + (i * 0.12);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(volume * 0.8, startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.5);
        osc.start(startTime);
        osc.stop(startTime + 0.55);
      });
      // Second burst for emphasis
      setTimeout(() => {
        if (ctx.state === 'running') {
          const secondNow = ctx.currentTime;
          [783.99, 987.77].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = 'triangle';
            const startTime = secondNow + (i * 0.1);
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(volume * 0.6, startTime + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);
            osc.start(startTime);
            osc.stop(startTime + 0.45);
          });
        }
      }, 550);
    } else if (type === 'report') {
      // Double chime for reports - more prominent
      const frequencies = [659.25, 783.99, 987.77]; // E5, G5, B5
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        const startTime = now + (i * 0.1);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(volume * 0.7, startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.5);
        osc.start(startTime);
        osc.stop(startTime + 0.55);
      });
      // Second set for emphasis
      setTimeout(() => {
        if (ctx.state === 'running') {
          const secondNow = ctx.currentTime;
          [783.99, 987.77].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';
            const startTime = secondNow + (i * 0.08);
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(volume * 0.5, startTime + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);
            osc.start(startTime);
            osc.stop(startTime + 0.35);
          });
        }
      }, 400);
    } else {
      // Default notification sound
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.35);
    }

    return true;
  } catch (error) {
    console.error('[NOTIFICATION_AUDIO] Play failed:', error);
    return false;
  }
};

// ============ CONTEXT ============
const NotificationSoundContext = createContext(null);

export const useNotificationSound = () => {
  const ctx = useContext(NotificationSoundContext);
  if (!ctx) {
    throw new Error('useNotificationSound must be used within NotificationSoundProvider');
  }
  return ctx;
};

export const NotificationSoundProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const pollIntervalRef = useRef(null);
  const lastCheckRef = useRef(null);
  // User's per-category tone preferences (mirrored from localStorage).
  // We listen for the `notification-tones-changed` event the settings
  // page dispatches when prefs change, so playback uses the latest
  // selection without a page reload.
  const tonePrefsRef = useRef(loadTonePrefs());

  useEffect(() => {
    const handler = () => {
      tonePrefsRef.current = loadTonePrefs();
      // Custom tone blobs may have been added/deleted — drop the cached
      // object URLs so the next play creates fresh ones.
      clearObjectUrlCache();
    };
    window.addEventListener('notification-tones-changed', handler);
    return () => window.removeEventListener('notification-tones-changed', handler);
  }, []);

  // Initialize audio on first user interaction
  const initializeAudio = useCallback(() => {
    if (!audioInitialized) {
      const ctx = initNotificationAudio();
      if (ctx) {
        setAudioInitialized(true);
        console.log('[NOTIFICATION_AUDIO] Initialized');
      }
    }
  }, [audioInitialized]);

  // Map a built-in tone id to the legacy oscillator types so we can reuse
  // the existing `playNotificationSound` engine for built-ins. Custom
  // uploads bypass the oscillator entirely and play via HTMLAudioElement.
  const builtinToLegacyType = (builtinId) => {
    switch (builtinId) {
      case 'chime': return 'comment';
      case 'arpeggio': return 'new_lead_received';
      case 'double_chime': return 'report';
      case 'single_beep': return 'default';
      default: return 'default';
    }
  };

  // Play notification sound. The `type` argument is the backend
  // notification type ("comment", "new_lead_received", etc.). We look up
  // which CATEGORY that type belongs to, then play whatever tone the user
  // has chosen for that category — built-in (oscillator) or custom
  // (uploaded audio file via HTMLAudioElement).
  const playSound = useCallback((type = 'comment') => {
    if (!soundEnabled) return false;
    initializeAudio();

    const category = categoryForType(type);
    const pref = tonePrefsRef.current?.[category]
      || { kind: 'builtin', builtinId: 'chime' };

    if (pref.kind === 'silent') {
      return true; // user explicitly muted this category
    }

    if (pref.kind === 'custom' && pref.customId) {
      // Resolve the IndexedDB blob to an object URL and play it. We do
      // this async but return true synchronously so the caller can keep
      // moving — playback latency is dominated by the HTMLAudioElement,
      // not by the IDB read (which is cached after the first hit).
      // Resume the audio context first so HTMLAudioElement playback
      // isn't blocked by an unprimed audio output on first arrival.
      ensureAudioRunning().then(() => getCustomToneUrl(pref.customId)).then((url) => {
        if (!url) {
          // Custom audio was deleted out from under the pref. Fall back
          // to the category's default built-in so the user still hears
          // SOMETHING instead of silent surprise.
          playNotificationSound(builtinToLegacyType(pref.builtinId || 'chime'), 0.4);
          return;
        }
        try {
          const audio = new Audio(url);
          audio.volume = 0.6;
          audio.play().catch((e) => {
            // Most likely autoplay policy. Fall back to oscillator so
            // the user still gets *some* audible cue — those use the
            // already-running AudioContext which is exempt.
            console.warn('[NOTIFICATION_AUDIO] Custom playback blocked, falling back to built-in:', e);
            playNotificationSound(builtinToLegacyType(pref.builtinId || 'chime'), 0.4);
          });
        } catch (e) {
          console.warn('[NOTIFICATION_AUDIO] Custom playback error:', e);
          playNotificationSound(builtinToLegacyType(pref.builtinId || 'chime'), 0.4);
        }
      });
      return true;
    }

    // Built-in oscillator tone.
    return playNotificationSound(builtinToLegacyType(pref.builtinId || 'chime'), 0.4);
  }, [soundEnabled, initializeAudio]);

  // Play helper used by the settings preview button. Async because both
  // the AudioContext resume and the IndexedDB blob fetch are async, and
  // we want to surface real errors to the user (silent failures are how
  // "it doesn't work" bugs are born). Bypasses the global mute so the
  // user can audition tones even while sounds are off.
  const previewTone = useCallback(async (pref) => {
    if (!pref) {
      toast.error('No tone selected');
      return false;
    }
    if (pref.kind === 'silent') {
      toast.info('This category is set to silent — no sound will play');
      return true;
    }

    // Make sure the audio context is actually running before we touch
    // it. The first user gesture on the page primes this; clicking Play
    // itself qualifies, so this almost always resolves immediately.
    const ctx = await ensureAudioRunning();
    if (!ctx || ctx.state !== 'running') {
      toast.error('Audio is blocked by the browser. Click anywhere on the page first, then try again.');
      return false;
    }

    if (pref.kind === 'custom') {
      if (!pref.customId) {
        toast.error('No custom file selected for this category yet — upload one or pick from the library.');
        return false;
      }
      try {
        const url = await getCustomToneUrl(pref.customId);
        if (!url) {
          toast.error('That custom tone is missing — re-upload or pick a different one.');
          return false;
        }
        const audio = new Audio(url);
        audio.volume = 0.6;
        await audio.play();
        return true;
      } catch (e) {
        toast.error(`Couldn't play custom tone: ${e?.message || 'unknown error'}`);
        return false;
      }
    }

    // Built-in oscillator tone.
    const ok = playNotificationSound(builtinToLegacyType(pref.builtinId || 'chime'), 0.4);
    if (!ok) {
      toast.error('Audio context not ready — click anywhere on the page first, then try again.');
    }
    return ok;
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token || !user) return;
    
    try {
      const response = await axios.get(
        `${API}/api/notifications?token=${token}&limit=20`
      );
      
      if (response.data.success) {
        const newNotifications = response.data.notifications || [];
        const newUnreadCount = response.data.unread_count || 0;
        
        // Check for new notifications
        if (lastCheckRef.current && newUnreadCount > unreadCount) {
          // New notification received - play sound
          const newestNotification = newNotifications[0];
          if (newestNotification) {
            const type = newestNotification.type || 'default';
            playSound(type);

            // Build a clickable toast. The destination depends on the
            // notification's related_type: contacts go to the lead; CEO
            // comments / shared briefs / direct feedback / voice notes go
            // to the appropriate dashboard for the recipient's role.
            const role = (user?.role || '').toLowerCase();
            const t = newestNotification.related_type;
            let destination = null;
            let actionLabel = null;
            if (t === 'contact' && newestNotification.related_id) {
              destination = `/contacts/${newestNotification.related_id}`;
              actionLabel = 'Open lead';
            } else if (t === 'ceo_comment' || t === 'brief') {
              destination = '/hod-dashboard?tab=ceo-comments';
              actionLabel = 'Open thread';
            } else if (t === 'feedback' || t === 'voice_message') {
              destination = role.startsWith('hod') || role === 'ceo'
                ? '/hod-dashboard?tab=ceo-comments'
                : '/my-dashboard#feedback';
              actionLabel = 'Open inbox';
            }
            const goTo = () => {
              if (destination) window.location.href = destination;
            };
            // Append a relative-time stamp to the description so each
            // toast carries its own "when did this fire" — without it
            // a burst of similar notifications (every Lead inactive
            // alert looks the same) is impossible to tell apart.
            const ts = formatRelative(newestNotification.created_at);
            const fullDescription = ts
              ? `${newestNotification.message}  ·  ${ts}`
              : newestNotification.message;
            toast(newestNotification.title, {
              description: fullDescription,
              duration: 8000,
              onAutoClose: () => {},
              onDismiss: () => {},
              ...(destination && {
                action: { label: actionLabel, onClick: goTo },
                onClick: goTo,
                className: 'cursor-pointer',
              }),
            });
          }
        }
        
        setNotifications(newNotifications);
        setUnreadCount(newUnreadCount);
        lastCheckRef.current = new Date();
      }
    } catch (error) {
      console.error('[NOTIFICATIONS] Fetch failed:', error);
    }
  }, [token, user, unreadCount, playSound]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;
    
    try {
      await axios.post(
        `${API}/api/notifications/${notificationId}/read?token=${token}`
      );
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[NOTIFICATIONS] Mark as read failed:', error);
    }
  }, [token]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/api/notifications/mark-all-read?token=${token}`);
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('[NOTIFICATIONS] Mark all as read failed:', error);
    }
  }, [token]);

  // One-shot fetch for browsing notification history with a date window.
  // Deliberately separate from `fetchNotifications` because the latter
  // also drives polling + new-notification sound; layering a date filter
  // on top of polling would silence new notifications that fall outside
  // the window. Callers (e.g. the dropdown when a date filter is active)
  // hold the result in their own state and don't touch the live feed.
  const fetchHistory = useCallback(async ({ dateFrom = '', dateTo = '', limit = 500 } = {}) => {
    if (!token) return [];
    try {
      const params = new URLSearchParams({ token, limit: String(limit) });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const response = await axios.get(`${API}/api/notifications?${params.toString()}`);
      if (response.data?.success) {
        return response.data.notifications || [];
      }
      return [];
    } catch (e) {
      console.error('Failed to fetch notification history:', e);
      return [];
    }
  }, [token]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
  }, []);

  // Test sound
  const testSound = useCallback((type = 'comment') => {
    initializeAudio();
    return playNotificationSound(type, 0.4);
  }, [initializeAudio]);

  // Set up polling when user is logged in
  useEffect(() => {
    if (token && user) {
      // Initial fetch
      fetchNotifications();
      lastCheckRef.current = new Date();
      
      // Poll every 10 seconds
      pollIntervalRef.current = setInterval(fetchNotifications, 10000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [token, user, fetchNotifications]);

  // Initialize audio on click anywhere
  useEffect(() => {
    const handleClick = () => {
      initializeAudio();
    };
    
    document.addEventListener('click', handleClick, { once: true });
    return () => document.removeEventListener('click', handleClick);
  }, [initializeAudio]);

  const value = {
    notifications,
    unreadCount,
    soundEnabled,
    audioInitialized,
    fetchNotifications,
    fetchHistory,
    markAsRead,
    markAllAsRead,
    toggleSound,
    playSound,
    previewTone,
    testSound,
  };

  return (
    <NotificationSoundContext.Provider value={value}>
      {children}
    </NotificationSoundContext.Provider>
  );
};

export default NotificationSoundContext;
